import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  Input, Output, ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {ReportProfile} from '../../../../model/archive/report-profile';
import {FormFieldConfig} from '../../../../model/api/form-field-config';
import {CcBaseComponent} from "../../../../crystal-elements/cc-base.component";
import {TableColumnConfig} from "../../../../model/api/table-column-config";
import {TableRequest} from "../../../../model/api/table-request";
import {FileUtils} from "../../../../utils/file-utils";
import {TableButtonConfig} from "../../../../model/api/table-button-config";
import {ReportTask} from "../../../../model/archive/report-task";
import {ReportTaskService} from "../../../../services/archive/report-task.service";
import {ReportTaskLoadRequest} from "../../../../model/archive/report-task-load-request";
import {FormFieldGroupConfig} from "../../../../model/api/form-field-group-config";
import TYPE = FormFieldConfig.TYPE;
import {CcTableComponent} from "../../../../crystal-elements/table/cc-table.component";
import {AppConfigService} from "../../../../services/app-config.service";
import {EMPTY, exhaustMap, takeUntil, timer} from "rxjs";
import {catchError, filter} from "rxjs/operators";

@Component({
  selector: 'app-report-tasks-table',
  templateUrl: './report-tasks-table.component.html',
  styleUrls: ['./report-tasks-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportTasksTableComponent extends CcBaseComponent {


  tableConfig: TableColumnConfig[] = [];

  headerButtonConfig: TableButtonConfig[] = [];

  contextMenuConfig: MenuItem[] = [];
  formRecord: ReportTask;
  selectedRecords: ReportTask[] = [];
  records: ReportTask[] = [];
  recordsChange = new EventEmitter<ReportTask[]>();

  totalRecords: number = 0;
  inProgressCount: number = 0;

  tableLoading: boolean = true;
  forceReload: boolean = true;

  tableCurrentRequest: ReportTaskLoadRequest;
  tableCurrentRequestChange = new EventEmitter<TableRequest>();

  states = [
    ReportTask.STATE.TREATED, ReportTask.STATE.ERROR, ReportTask.STATE.IN_PROGRESS];
  @Input()
  transPartKey: string;
  @Input()
  origins: string[];
  @Input()
  visible: boolean = true;
  selectedProfile: ReportProfile;
  formConfig: FormFieldGroupConfig[];
  @Input()
  detailDialogVisible: boolean = false;
  @Output()
  detailDialogVisibleChange = new EventEmitter<boolean>();

  private reloadSub?: any;

  @ViewChild('ccTable')
  public ccTable: CcTableComponent;

  constructor(cdref: ChangeDetectorRef,
              componentFactoryResolver: ComponentFactoryResolver,
              messageService: MessageService,
              protected reportTaskService: ReportTaskService,
              protected appConfigService: AppConfigService,
              translateService: TranslateService) {
    super(cdref, messageService, translateService);

  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.initConfigs();
    // setTimeout(() => {
      this.startReloadRecordsLoop();
    // });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  protected initConfigs() {
    super.initConfigs();

    function getRowClass(record: ReportTask) {
      if (ReportTask.STATE.TREATED === record.state)
        return "task-treaded";
      else if (ReportTask.STATE.ERROR === record.state)
        return "task-error";
      else if (ReportTask.STATE.IN_PROGRESS === record.state)
        return "task-inprogress";
    }

    this.tableConfig = [
      {
        field: 'creationDate',
        format: 'dd/MM/yyyy HH:mm:ss',
        type: 'DATE',
        class: record => getRowClass(record),
        notFilterable: true
      },
      {
        field: 'profile',
        mapId: record => record.reportProfileJson ? JSON.parse(record.reportProfileJson).name : '',
        class: record => getRowClass(record),
        notFilterable: true,
        notSortable: true
      },
      {
        field: 'template',
        mapId: record => record.reportProfileJson ? JSON.parse(record.reportProfileJson).exportTemplate : '',
        class: record => getRowClass(record),
        notFilterable: true,
        notSortable: true
      },
      {
        field: 'state',
        class: record => getRowClass(record),
      },
      {
        field: 'message',
        toolTip: record => record.message,
        class: record => getRowClass(record),
      }]
    // if (this.isAdmin()) {
    this.tableConfig.push({
      field: 'ccOperator',
      class: record => getRowClass(record),
    });
    // }
    if (this.origins.length > 1) {
      this.tableConfig.push({
        field: 'origin',
        class: record => getRowClass(record),
      });
    }


    let simplified = !this.isAdmin();
    this.headerButtonConfig = [
      {
        tooltip: this.translateService.instant('common.table.download_button'),
        icon: 'fas fa-file-download',
        class: ' p-button-success',
        command: records => this.download(),
        enabled: () => this.selectedRecords && this.hasOneSelection() && ReportTask.STATE.TREATED === this.selectedRecords[0].state
      }
    ];
    this.contextMenuConfig = [
      {
        id: 'download',
        label: this.translateService.instant('common.table.download_button'),
        icon: 'fas fa-file-download',
        command: () => {
          this.download();
        }
      },
    ];
    if (!simplified) {
      this.headerButtonConfig.push(
        {
          tooltip: this.translateService.instant('common.table.detail_button'),
          icon: 'fas fa-eye',
          class: ' p-button-success',
          command: records => this.showDialogForm(this.selectedRecords[0]),
          enabled: () => this.selectedRecords && this.hasOneSelection()
        });
      this.contextMenuConfig.push(
        {
          label: this.translateService.instant('common.table.detail_button'),
          icon: 'fas fa-eye',
          command: () => {
            this.showDialogForm(this.formRecord);
          }
        }
      );
    }
    this.formConfig = [];
    const groupFields = [];
    if (!simplified) {
      groupFields.push({
        field: 'active',
        column: 2,
        type: TYPE.CHECKBOX,
      });
    }
    this.formConfig.push({
      group: '',
      fields: groupFields
    });
    groupFields.push({
        field: 'name',
        column: 3,
        type: TYPE.TEXT,
      },
      {
        field: 'title',
        column: 3,
        type: TYPE.TEXT,
      },
      {
        field: 'visibility',
        'required': true,
        column: 2,
        type: TYPE.TEXT,
      },
      {
        type: TYPE.NONE,
        column: 2,
      });
    this.formConfig.push({
      group: 'template',
      fields:
        [{
          field: 'exportTemplate',
          'required': true,
          column: 3,
          type: TYPE.TEXT,
        },
          {
            field: 'exportFormat',
            'required': true,
            column: 3,
            type: TYPE.TEXT,
          },
          {
            field: 'exportDateMode',
            'required': true,
            column: 3,
            type: TYPE.TEXT,
          },
          {
            field: 'zipFile',
            column: 2,
            type: TYPE.CHECKBOX,
          },
          {
            type: TYPE.NONE,
            column: 1,
          },
          {
            field: 'filterMap',
            column: 12,
            type: TYPE.MAPFIELD
          }
        ]
    })
    if (!simplified) {
      this.formConfig.push({
          group: 'schedule',
          fields:
            [{
              field: 'executionTimesJS',
              column: 3,
              type: TYPE.MULTISELECT
            },
            {
              field: 'executionDaysOfMonthJS',
              column: 3,
              type: TYPE.MULTISELECT,
            },
            {
              field: 'executionCron',
              column: 3,
              type: TYPE.CRON,
            }]
        },
        {
          group: 'transfer',
          fields:
            [{
              field: 'transferMailSubject',
              column: 4,
              type: TYPE.TEXT,
            },
              {
                field: 'transferMailTemplate',
                column: 3,
                type: TYPE.TEXT
              },
              {
                column: 5,
                type: TYPE.NONE,
              },
              {
                field: 'transferMailRecipients',
                column: 12,
                type: TYPE.MULTITEXT,
              },
              {
                field: 'transferFileLocations',
                column: 6,
                type: TYPE.MULTISELECT,
              }
            ]
        },
        {
          group: 'alert',
          fields:
            [{
              field: 'mailAlert',
              column: 3,
              type: TYPE.CHECKBOX
            }]
        });
    }
  }

  // loadRecords(request ?: ReportTaskLoadRequest) {
  //   this.tableLoading = true;
  //   this.reLoadRecords(request);
  // }
  startReloadRecordsLoop(): void {
    const intervalMs = this.appConfigService.reportTaskReloadInterval;

    // optional: ensure only one loop runs
    this.reloadSub?.unsubscribe();

    this.reloadSub = timer(0, intervalMs)
      .pipe(
        takeUntil(this.destroy$),

        // If no request, just do nothing on ticks
        filter(() => !!this.tableCurrentRequest),

        // One list() call at a time (no overlap)
        exhaustMap(() =>
          this.reportTaskService.list(this.tableCurrentRequest!).pipe(
            catchError(err => {
              this.errorMessage(String(err));
              this.tableLoading = false;
              this.markForCheck();
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe(tableRecordResult => {
        if (tableRecordResult.success) {
          if (this.forceReload || this.totalRecords !== tableRecordResult.total) {
            this.records.splice(0, this.records.length);
            this.forceReload = false;
          }

          this.totalRecords = tableRecordResult.total;
          this.inProgressCount = 0;

          this.applyRecordResult(
            tableRecordResult
          );

          this.records = [...this.records];
          this.recordsChange.emit(this.records);

          this.apiMessage(tableRecordResult);

          this.tableLoading = false;
          this.markForCheck();
        } else {
          this.errorMessage(tableRecordResult.message);
          this.tableLoading = false;
        }

        // refresh metadata
        this.tableCurrentRequest!.syncMetadata = tableRecordResult.syncMetadata;
      });
  }

  // private applyRecordResult(tableRecordResult, from: number, to: number) {
  //   let hasChanged: boolean = false;
  //   tableRecordResult.records.forEach(newRecord => {
  //     const recordIndex = this.records.findIndex(record => record.id === newRecord.id);
  //     if (recordIndex >= 0) {
  //       for (let key in newRecord) {
  //         this.records[recordIndex][key] = newRecord[key];
  //       }
  //     } else {
  //       this.records.splice(0, 0, newRecord);
  //       hasChanged = true;
  //     }
  //   });
  //   return hasChanged;
  // }

  private applyRecordResult(tableRecordResult): boolean {
    const changed =
      JSON.stringify(this.records.map(r => r.id))
      !== JSON.stringify(tableRecordResult.records.map(r => r.id));

    if (changed) {
      this.records = [...tableRecordResult.records];
      return true;
    }

    tableRecordResult.records.forEach((newRecord, i) => {
      Object.assign(this.records[i], newRecord);
    });

    return false;
  }

  hasOneSelection(): boolean {
    return this.selectedRecords != undefined && this.selectedRecords.length == 1;
  }

  download(): void {
    if (this.selectedRecords) {
      this.formRecord = Object.assign({}, this.selectedRecords[0]);
    }
    if (this.formRecord) {
      if (ReportTask.STATE.TREATED === this.formRecord.state) {
        this.reportTaskService.download(this.formRecord.id).subscribe(apiFileResponse => {
          if (apiFileResponse.success) {
            FileUtils.download(apiFileResponse);
          } else {
            this.successMessage(apiFileResponse.message);
          }
          this.tableLoading = false;
        });
      } else {
        this.infoMessage(this.getTranslation('page.' + this.transPartKey + '.message.not_treated'));
      }
    }
  }

  onRowDblClick($event: any) {
    if (this.isAdmin())
      this.showDialogForm(this.selectedRecords[0]);
    else
      this.download();
  }

  showDialogForm(record: ReportTask): void {
    this.selectedProfile = Object.assign({}, JSON.parse(record.reportProfileJson));
    this.detailDialogVisible = true;
    // this.detailDialogVisibleChange.emit(true);
  }

  closeDialogForm(): void {
    this.detailDialogVisible = false;
    // this.detailDialogVisibleChange.emit(false);
  }

  needLoadRecords(request: ReportTaskLoadRequest) {
    this.tableLoading = true;

    if (this.tableCurrentRequest && ((this.tableCurrentRequest.filters.length === 0 && request.filters.length > 0)
      || (JSON.stringify(this.tableCurrentRequest.filters) != JSON.stringify(request.filters))
      || (this.tableCurrentRequest.sortField !== request.sortField)
      || (this.tableCurrentRequest.sortOrder != request.sortOrder)
      || (this.tableCurrentRequest.globalFilter !== request.globalFilter))) {
      this.totalRecords = 0;
      this.forceReload = true;
    }
    if (request) {
      this.tableCurrentRequest = request;
      this.tableCurrentRequest.states = this.states;
      this.tableCurrentRequest.origins = this.origins;
    }
    if (this.tableCurrentRequest && !this.tableCurrentRequest.sortOrder) {
      this.tableCurrentRequest.sortOrder = -1;
      this.tableCurrentRequest.sortField = 'creationDate';
    }
  }

  refreshContextMenu() {
    this.contextMenuConfig.forEach(menu => {
      if (menu.id === 'download') {
        menu.disabled = !this.hasOneSelection() || (this.hasOneSelection() && ReportTask.STATE.TREATED != this.selectedRecords[0].state);
      }
    });
    this.contextMenuConfig = [...this.contextMenuConfig];
    this.markForCheck();
  }
}
